import { Auth } from 'aws-amplify';
import axios from 'axios';
import { isEmpty } from 'utils/helpers/lodash';

import awsExports from 'public/assets/aws-exports.json';

// eslint-disable-next-line import-helpers/order-imports
import { AmplifyTokens, AwsExports } from './types';

// eslint-disable-next-line import/order
import { CognitoUser } from 'types/others';

const getAwsExports = async (): Promise<AwsExports> => {
  const env = process.env.NODE_ENV;

  if (env === 'development') return awsExports;

  try {
    const { data } = await axios.get<AwsExports>(
      '/new-provider/assets/aws-exports.json',
    );
    return data;
  } catch (e) {
    return {} as AwsExports;
  }
};

const getAuthorizationTokens = async (): Promise<AmplifyTokens> => {
  try {
    const currentSession = await Auth.currentSession();

    return {
      accessToken: currentSession.getAccessToken().getJwtToken(),
      idToken: currentSession.getIdToken().getJwtToken(),
      refreshToken: currentSession.getRefreshToken().getToken(),
    };
  } catch (e) {
    return {
      accessToken: null,
      idToken: null,
      refreshToken: null,
    };
  }
};

const getCurrentUserInfo = async () => {
  try {
    const user = await Auth.currentUserInfo();

    return user;
  } catch (error) {
    return null;
  }
};

const isUserAuthenticated = async () => {
  try {
    const currentUserInfo = await Auth.currentUserInfo();
    return !isEmpty(currentUserInfo);
  } catch {
    return false;
  }
};

const setLogin = async (
  email: string,
  password: string,
): Promise<CognitoUser> => {
  try {
    const user = await Auth.signIn(email, password);
    return user;
  } catch (e) {
    if (e instanceof Error && e.message.includes('password has expired')) {
      return 'expired';
    }
    return null;
  }
};

const sendForgotPasswordEmail = async (email: string) => {
  try {
    return await Auth.forgotPassword(email);
  } catch {
    return null;
  }
};

const updateTemporaryPassword = async (
  user: CognitoUser,
  confirmNewPassword: string,
) => {
  try {
    return await Auth.completeNewPassword(user, confirmNewPassword);
  } catch {
    return null;
  }
};

const updatePassword = async (
  email: string,
  confirmationCode: string,
  confirmNewPassword: string,
) => {
  try {
    return await Auth.forgotPasswordSubmit(
      email,
      confirmationCode,
      confirmNewPassword,
    );
  } catch {
    return null;
  }
};

const getCurrentSpecialistRoles = async () => {
  const currentSession = await Auth.currentSession();
  const accessToken = currentSession.getAccessToken();
  return accessToken.decodePayload()['cognito:groups'];
};

export {
  setLogin,
  getAwsExports,
  updatePassword,
  getCurrentUserInfo,
  isUserAuthenticated,
  getAuthorizationTokens,
  updateTemporaryPassword,
  sendForgotPasswordEmail,
  getCurrentSpecialistRoles,
};
